import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useT, type TKey } from '../lib/i18n';

/**
 * The tour of the app: one thing at a time, in big type, with the rest of the screen dimmed. It
 * points at elements marked `data-tour="…"` (the rail on desktop, the bottom bar on phones, the
 * buttons on Vandaag); a step whose element isn't on screen shows as a card in the middle.
 */
export type TourStep = { id: string; target?: string; title: TKey; body: TKey };

export function tourSteps(role: 'owner' | 'manager' | 'staff'): TourStep[] {
  const staff = role === 'staff';
  return [
    { id: 'welcome', title: 'tour.welcome.title', body: 'tour.welcome.body' },
    { id: 'today', target: 'today', title: 'tour.today.title', body: staff ? 'tour.today.bodyStaff' : 'tour.today.body' },
    { id: 'agenda', target: 'agenda', title: 'tour.agenda.title', body: 'tour.agenda.body' },
    { id: 'new', target: 'new', title: 'tour.new.title', body: 'tour.new.body' },
    { id: 'clients', target: 'clients', title: 'tour.clients.title', body: 'tour.clients.body' },
    ...(staff
      ? []
      : ([
          { id: 'salon', target: 'salon', title: 'tour.salon.title', body: 'tour.salon.body' },
          { id: 'share', target: 'share', title: 'tour.share.title', body: 'tour.share.body' },
        ] as TourStep[])),
    { id: 'help', target: 'help', title: 'tour.help.title', body: 'tour.help.body' },
    { id: 'done', title: 'tour.done.title', body: staff ? 'tour.done.bodyStaff' : 'tour.done.body' },
  ];
}

/** The first element with this marker that is actually on screen (phone and desktop each have their own). */
function findTarget(name: string) {
  return [...document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`)].find((el) => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
  });
}

type Box = { top: number; left: number; width: number; height: number };
const PAD = 8;
const CARD_W = 360;

export function Tour({ steps, onClose }: { steps: TourStep[]; onClose: (finished: boolean) => void }) {
  const t = useT();
  const [i, setI] = useState(0);
  const [box, setBox] = useState<Box | null>(null);
  const [view, setView] = useState({ w: window.innerWidth, h: window.innerHeight });
  const nextButton = useRef<HTMLButtonElement>(null);
  const step = steps[i]!;
  const last = i === steps.length - 1;

  // Measure the highlighted element (after scrolling it into view), and again on resize or scroll.
  useLayoutEffect(() => {
    const el = step.target ? findTarget(step.target) : undefined;
    if (!el) return setBox(null);
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    const measure = () => {
      const r = el.getBoundingClientRect();
      setBox({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
      setView({ w: window.innerWidth, h: window.innerHeight });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step]);

  useEffect(() => nextButton.current?.focus(), [i]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose(false);
      if (e.key === 'ArrowRight') setI((n) => Math.min(n + 1, steps.length - 1));
      if (e.key === 'ArrowLeft') setI((n) => Math.max(n - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, steps.length]);

  // Where the card goes: beside the left rail, above something low on the screen, below the rest.
  const phone = view.w < 768;
  const cardW = Math.min(CARD_W, view.w - 24);
  let style: React.CSSProperties;
  if (!box) style = { left: (view.w - cardW) / 2, top: Math.max(16, view.h / 2 - 170), width: cardW };
  else if (!phone && box.left + box.width < 140) style = { left: box.left + box.width + 16, top: Math.min(Math.max(16, box.top - 20), view.h - 300), width: cardW };
  else {
    const left = Math.min(Math.max(12, box.left + box.width / 2 - cardW / 2), view.w - cardW - 12);
    style = box.top + box.height / 2 > view.h / 2 ? { left, bottom: view.h - box.top + 14, width: cardW } : { left, top: box.top + box.height + 14, width: cardW };
  }

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-labelledby="tour-title" aria-describedby="tour-body">
      {/* Clicks outside the card do nothing: the tour only moves with its own buttons. */}
      <div className="absolute inset-0" />
      {box ? (
        <div
          aria-hidden
          className="pointer-events-none absolute rounded-[20px] transition-all duration-300"
          // One shadow for both: the cream ring around the spot, and the dimmed screen around that.
          style={{ ...box, boxShadow: '0 0 0 4px var(--color-cream), 0 0 0 9999px rgb(1 32 25 / 0.66)' }}
        />
      ) : (
        <div aria-hidden className="absolute inset-0 bg-[rgb(1_32_25/0.66)]" />
      )}
      <div className="absolute flex flex-col gap-4 rounded-[22px] bg-surface p-5 text-ink shadow-[0_24px_60px_rgb(0_0_0/0.35)] sm:p-6" style={style}>
        <div className="flex items-center justify-between gap-3">
          <p className="text-[13px] font-semibold text-muted">{t('tour.progress', { n: i + 1, total: steps.length })}</p>
          {!last && (
            <button type="button" onClick={() => onClose(false)} className="rounded-full px-2 py-1 text-[14px] font-medium text-muted hover:bg-subtle hover:text-ink">
              {t('tour.skip')}
            </button>
          )}
        </div>
        <div aria-live="polite">
          <h2 id="tour-title" className="text-[24px] font-bold leading-tight tracking-tight">
            {t(step.title)}
          </h2>
          <p id="tour-body" className="mt-2 text-[17px] leading-relaxed">
            {t(step.body)}
          </p>
        </div>
        {/* The steps as dots: you always see how far along you are. */}
        <div aria-hidden className="flex gap-1.5">
          {steps.map((s, n) => (
            <span key={s.id} className={`h-1.5 rounded-full transition-all ${n === i ? 'w-6 bg-accent' : n < i ? 'w-1.5 bg-ink/40' : 'w-1.5 bg-line'}`} />
          ))}
        </div>
        <div className="flex items-center gap-2">
          {i > 0 && !last && (
            <button type="button" onClick={() => setI(i - 1)} className="min-h-12 rounded-full px-4 text-[16px] font-semibold text-muted hover:bg-subtle hover:text-ink">
              {t('tour.back')}
            </button>
          )}
          <button
            ref={nextButton}
            type="button"
            onClick={() => (last ? onClose(true) : setI(i + 1))}
            className="ml-auto min-h-12 flex-1 rounded-full bg-accent px-6 text-[17px] font-bold text-on-accent hover:bg-accent-strong hover:text-white sm:flex-none"
          >
            {last ? t('tour.finish') : t('tour.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
